import axios from 'axios'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Redirect } from 'react-router-dom'

import { MainButton } from '../../components'
import { api } from '../../services/api'
import { isNonProductionEnvironment } from '../../utils/environment'
import { purple } from '../../utils/colors'

const CRON_TOKEN =
	process.env.REACT_APP_CRON_TOKEN || 'thisIsTheCronJobTokenWheeeLookAtMe82987129817509235'
const API_BASE = process.env.REACT_APP_API_URI

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const useStyles = makeStyles((theme) => ({
	root: {
		padding: '1.5rem'
	},
	header: {
		paddingBottom: '1.5rem',
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	title: {
		fontSize: 22,
		color: purple[900]
	},
	content: {
		marginTop: '1.5rem',
		maxWidth: 480
	},
	formControl: {
		width: '100%',
		marginBottom: '1.5rem'
	},
	buttonRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0.75rem'
	},
	loadingPanel: {
		marginTop: '2rem',
		padding: '2rem',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '1rem',
		textAlign: 'center'
	},
	warning: {
		fontWeight: 700,
		color: purple[900]
	},
	status: {
		color: theme.palette.text.secondary
	},
	error: {
		marginTop: '1rem',
		color: theme.palette.error.main
	},
	success: {
		marginTop: '1rem',
		color: theme.palette.success.main
	}
}))

const PAYMENT_JOBS = [
	{ path: 'useCredits', label: 'useCredits' },
	{ path: 'payments', label: 'payments', waitBeforeMs: 10000 },
	{ path: 'cancellations', label: 'cancellations' },
	{ path: 'homeServicePayments', label: 'home service payments' },
	{ path: 'walkBankRefill', label: 'walk bank refill' },
	{ path: 'addOnBank', label: 'add-on bank' },
	{ path: 'renewals', label: 'membership renewals' },
	{ path: 'convertUpcoming', label: 'convert upcoming memberships' }
]

const runScheduledJob = async (jobPath, facilityId) => {
	return axios.post(
		`${API_BASE}/scheduled/${jobPath}?facilityId=${facilityId}`,
		{},
		{
			headers: {
				Authorization: `Bearer ${CRON_TOKEN}`
			}
		}
	)
}

const RunPayments = () => {
	const classes = useStyles()
	const facilities = useSelector((state) => state.specific.facilities.facilities) || []

	const [selectedFacilityId, setSelectedFacilityId] = useState('')
	const [loading, setLoading] = useState(false)
	const [statusMessage, setStatusMessage] = useState('')
	const [errorMessage, setErrorMessage] = useState('')
	const [successMessage, setSuccessMessage] = useState('')

	const sortedFacilities = useMemo(() => {
		return [...facilities].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	}, [facilities])

	if (!isNonProductionEnvironment()) {
		return <Redirect to="/globalAdmin/markets" />
	}

	const handleRunPayments = async () => {
		if (!selectedFacilityId || loading) return

		setLoading(true)
		setErrorMessage('')
		setSuccessMessage('')

		try {
			for (const job of PAYMENT_JOBS) {
				if (job.waitBeforeMs) {
					setStatusMessage(`Waiting ${job.waitBeforeMs / 1000} seconds before running ${job.label}...`)
					await sleep(job.waitBeforeMs)
				}

				setStatusMessage(`Running ${job.label}...`)
				await runScheduledJob(job.path, selectedFacilityId)
			}

			setSuccessMessage('Payment jobs triggered successfully.')
		} catch (error) {
			const responseData = error?.response?.data
			const message =
				responseData?.message ||
				(typeof responseData === 'string' ? responseData : null) ||
				error?.message ||
				'Failed to trigger payment jobs. Please try again.'
			setErrorMessage(message)
		} finally {
			setLoading(false)
			setStatusMessage('')
		}
	}

	const handleRunAssignments = async () => {
		if (!selectedFacilityId || loading) return

		const today = moment().format('YYYY-MM-DD')
		const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD')

		setLoading(true)
		setErrorMessage('')
		setSuccessMessage('')

		try {
			setStatusMessage(`Auto-assigning cubbies for ${today}...`)
			await api.post(`/facilities/autoAssignCubbies/${selectedFacilityId}`, { date: today })

			setStatusMessage(`Auto-assigning cubbies for ${tomorrow}...`)
			await api.post(`/facilities/autoAssignCubbies/${selectedFacilityId}`, { date: tomorrow })

			setStatusMessage(`Auto-assigning playgroups and kennels for ${today}...`)
			const { data: dogs } = await api.post(
				`/facilities/autoAssignPlaygroups/123?date=${today}&facilityId=${selectedFacilityId}`
			)
			await api.post(`/facilities/assignDogs?date=${today}&facilityId=${selectedFacilityId}`, {
				dogs
			})

			setSuccessMessage('Kennel and cubby assignment jobs completed.')
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.data?.message ||
				error?.message ||
				'Failed to run assignment jobs. Please try again.'
			setErrorMessage(message)
		} finally {
			setLoading(false)
			setStatusMessage('')
		}
	}

	return (
		<div className={classes.root}>
			<div className={classes.header}>
				<Typography className={classes.title}>Run Payments</Typography>
				<Typography variant="body2" color="textSecondary">
					Trigger payments, kennel, and cubby assignment jobs for a facility. Staging and development
					only.
				</Typography>
			</div>

			<div className={classes.content}>
				<FormControl variant="outlined" className={classes.formControl}>
					<InputLabel id="run-payments-facility-label">Facility</InputLabel>
					<Select
						labelId="run-payments-facility-label"
						label="Facility"
						value={selectedFacilityId}
						onChange={(event) => {
							setSelectedFacilityId(event.target.value)
							setErrorMessage('')
							setSuccessMessage('')
						}}
						disabled={loading}>
						<MenuItem value="">
							<em>Select a facility</em>
						</MenuItem>
						{sortedFacilities.map((facility) => (
							<MenuItem key={facility._id} value={facility._id}>
								{facility.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{selectedFacilityId && !loading && (
					<div className={classes.buttonRow}>
						<MainButton label="Run Payments" onClick={handleRunPayments} />
						<MainButton label="Run Kennel & Cubby Assignments" onClick={handleRunAssignments} />
					</div>
				)}

				{errorMessage && (
					<Typography className={classes.error}>{errorMessage}</Typography>
				)}

				{successMessage && !loading && (
					<Typography className={classes.success}>{successMessage}</Typography>
				)}
			</div>

			{loading && (
				<Paper className={classes.loadingPanel} elevation={2}>
					<CircularProgress />
					<Typography className={classes.warning}>Please don&apos;t leave this page</Typography>
					{statusMessage && (
						<Typography className={classes.status}>{statusMessage}</Typography>
					)}
				</Paper>
			)}
		</div>
	)
}

export default RunPayments
